import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Alert, InputGroup, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import axios from './axiosConfig';
import './UserPanel.css';

const NewSnmpCredential: React.FC = () => {
  const [user, setUser] = useState<string>('');
  const [authPrivLevel, setAuthPrivLevel] = useState<string>('authPriv');
  const [authProtocol, setAuthProtocol] = useState<string>('SHA');
  const [authPassphrase, setAuthPassphrase] = useState<string>('');
  const [encryptionProtocol, setEncryptionProtocol] = useState<string>('AES');
  const [privPassphrase, setPrivPassphrase] = useState<string>('');
  const [targetIP, setTargetIP] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<any[]>([]);

  const [showAuthPassphrase, setShowAuthPassphrase] = useState<boolean>(false);
  const [showPrivPassphrase, setShowPrivPassphrase] = useState<boolean>(false);

  const fetchCredentials = async () => {
    try {
      const response = await axios.get('/api/snmp-credentials');
      setCredentials(response.data);
    } catch (error) {
      console.error('Failed to fetch SNMP credentials:', error);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const response = await axios.post('/api/snmp-credentials', {
        user,
        auth_priv_level: authPrivLevel,
        auth_protocol: authProtocol,
        auth_passphrase: authPassphrase,
        encryption_protocol: encryptionProtocol,
        priv_passphrase: privPassphrase,
        target_ip: targetIP,
      });

      if (response.status === 200) {
        setSuccessMessage('SNMP Credential created successfully!');
        setError(null);

        // Clear form fields
        setUser('');
        setAuthPrivLevel('authPriv');
        setAuthProtocol('SHA');
        setAuthPassphrase('');
        setEncryptionProtocol('AES');
        setPrivPassphrase('');
        setTargetIP('');

        // Fetch updated credentials
        fetchCredentials();
      }
    } catch (error) {
      setError('Failed to create SNMP credential. Please try again.');
      setSuccessMessage(null);
    }
  };

  return (
    <Container>
      <h1>Create SNMP Credential</h1>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      <Form onSubmit={handleSubmit}>
        {/* Form fields for SNMP credentials */}
        <Form.Group controlId="formUser">
          <Form.Label>User</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter SNMP user"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            required
            onInvalid={(e) => e.currentTarget.setCustomValidity('Please enter the SNMP user')}
            onInput={(e) => e.currentTarget.setCustomValidity('')}
          />
        </Form.Group>

        <Form.Group controlId="formAuthPrivLevel">
          <Form.Label>AuthPriv Level</Form.Label>
          <Form.Select
            value={authPrivLevel}
            onChange={(e) => setAuthPrivLevel(e.target.value)}
            required
            onInvalid={(e) => e.currentTarget.setCustomValidity('Please select an AuthPriv Level')}
            onInput={(e) => e.currentTarget.setCustomValidity('')}
          >
            <option value="authPriv">authPriv</option>
            <option value="authNoPriv">authNoPriv</option>
            <option value="noAuthNoPriv">noAuthNoPriv</option>
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="formAuthProtocol">
          <Form.Label>Authentication Protocol</Form.Label>
          <Form.Select
            value={authProtocol}
            onChange={(e) => setAuthProtocol(e.target.value)}
            required
            onInvalid={(e) => e.currentTarget.setCustomValidity('Please select an Authentication Protocol')}
            onInput={(e) => e.currentTarget.setCustomValidity('')}
          >
            <option value="SHA">SHA</option>
            <option value="MD5">MD5</option>
          </Form.Select>
        </Form.Group>

        {/* Authentication Passphrase with Eye Icon */}
        <Form.Group controlId="formAuthPassphrase">
          <Form.Label>Authentication Passphrase</Form.Label>
          <InputGroup>
            <Form.Control
              type={showAuthPassphrase ? "text" : "password"}
              placeholder="Enter Authentication Passphrase"
              value={authPassphrase}
              onChange={(e) => setAuthPassphrase(e.target.value)}
              required
              onInvalid={(e) => e.currentTarget.setCustomValidity('Please enter the Authentication Passphrase')}
              onInput={(e) => e.currentTarget.setCustomValidity('')}
            />
            <InputGroup.Text onClick={() => setShowAuthPassphrase(!showAuthPassphrase)}>
              <FontAwesomeIcon icon={showAuthPassphrase ? faEyeSlash : faEye} />
            </InputGroup.Text>
          </InputGroup>
        </Form.Group>

        <Form.Group controlId="formEncryptionProtocol">
          <Form.Label>Encryption Protocol</Form.Label>
          <Form.Select
            value={encryptionProtocol}
            onChange={(e) => setEncryptionProtocol(e.target.value)}
            required
            onInvalid={(e) => e.currentTarget.setCustomValidity('Please select an Encryption Protocol')}
            onInput={(e) => e.currentTarget.setCustomValidity('')}
          >
            <option value="AES">AES</option>
            <option value="DES">DES</option>
          </Form.Select>
        </Form.Group>

        {/* Privacy Passphrase with Eye Icon */}
        <Form.Group controlId="formPrivPassphrase">
          <Form.Label>Privacy Passphrase</Form.Label>
          <InputGroup>
            <Form.Control
              type={showPrivPassphrase ? "text" : "password"}
              placeholder="Enter Privacy Passphrase"
              value={privPassphrase}
              onChange={(e) => setPrivPassphrase(e.target.value)}
              required
              onInvalid={(e) => e.currentTarget.setCustomValidity('Please enter the Privacy Passphrase')}
              onInput={(e) => e.currentTarget.setCustomValidity('')}
            />
            <InputGroup.Text onClick={() => setShowPrivPassphrase(!showPrivPassphrase)}>
              <FontAwesomeIcon icon={showPrivPassphrase ? faEyeSlash : faEye} />
            </InputGroup.Text>
          </InputGroup>
        </Form.Group>

        <Form.Group controlId="formTargetIP">
          <Form.Label>Target IP</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter Target IP"
            value={targetIP}
            onChange={(e) => setTargetIP(e.target.value)}
            required
            onInvalid={(e) => e.currentTarget.setCustomValidity('Please enter the Target IP')}
            onInput={(e) => e.currentTarget.setCustomValidity('')}
          />
        </Form.Group>

        <Button variant="primary" type="submit">
          Create SNMP Credential
        </Button>
      </Form>

      {/* Display existing SNMP credentials */}
      <h2 className="mt-4">Existing SNMP Credentials</h2>
      {credentials.length > 0 ? (
        <Table striped bordered hover size="sm" responsive variant="dark">
          <thead>
            <tr>
              <th>User</th>
              <th>AuthPriv Level</th>
              <th>Auth Protocol</th>
              <th>Encryption Protocol</th>
              <th>Target IP</th>
            </tr>
          </thead>
          <tbody>
            {credentials.map((credential, index) => (
              <tr key={index}>
                <td>{credential.user}</td>
                <td>{credential.auth_priv_level}</td>
                <td>{credential.auth_protocol}</td>
                <td>{credential.encryption_protocol}</td>
                <td>{credential.target_ip}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>No SNMP credentials found.</p>
      )}
    </Container>
  );
};

export default NewSnmpCredential;
